import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'
import { Firestore } from '@angular/fire/firestore'
import { HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import {
    Client,
    CreatePaymentMethodRequest,
    CreatePaymentMethodResponse,
    FollowupRequestRequest,
    FollowupRequestResponse,
    PayGateClient,
    PayGateMpiResponse,
    PayGateResultCode,
    PaymentMethod,
    ProcessTokenPaymentRequest,
    ProcessTokenPaymentResponse,
    SettlementRequestRequest,
    SlackChannel,
    Subscription,
} from '@parkupp/core'
import { AuthenticationService } from './authentication.service'
import { firstValueFrom } from 'rxjs'
import { BackendService } from './backend.service'

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private urlCreatePaymentMethod = `${environment.functionsUrl}/payGate-httpCreatePaymentMethod`
    private urlProcessTokenPayment = `${environment.functionsUrl}/payGate-httpProcessTokenPayment`
    private httpSettlementRequestUrl = `${environment.functionsUrl}/payGate-httpSettlementRequest`
    private urlFollowupRequest = `${environment.functionsUrl}/payGate-httpFollowupRequest`
    private urlVoidRequest = `${environment.functionsUrl}/payGate-httpVoidRequest`

    constructor(private authSvc: AuthenticationService, public firestore: Firestore, private router: Router, private http: HttpClient, private backendService: BackendService) {}

    /**
     * Ask the backend to start a PayWeb3 hosted card capture. No card data is sent — the
     * cardholder enters it on PayGate's page.
     *
     * `subscriptionKey` is omitted when the client adds a card from their Payment Methods
     * page; the backend then links the R1 tokenisation payment to the client alone.
     */
    httpCreatePaymentMethod(subscriptionKey: string | undefined, client: Client): Promise<CreatePaymentMethodResponse> {
        const payGateClient = new PayGateClient({
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.$email,
            mobile: client.$phoneNumber,
            key: client.$key,
        })

        const request = new CreatePaymentMethodRequest({ subscriptionKey, payGateClient })

        return firstValueFrom(this.http.post<CreatePaymentMethodResponse>(this.urlCreatePaymentMethod, request)).catch((error: any) => {
            console.error('httpCreatePaymentMethod failed', error)
            return new CreatePaymentMethodResponse({
                error: true,
                errorMessage: error?.error?.errorMessage || 'Could not start card capture. Please try again.',
            })
        })
    }

    async httpProcessTokenPaymentCreateSubscription(selectedPaymentMethod: PaymentMethod, subscriptionKey: string): Promise<ProcessTokenPaymentResponse | { error: boolean; errorMessage?: string }> {
        // Create the request payload
        let post = new ProcessTokenPaymentRequest({
            paymentMethodKey: selectedPaymentMethod.$key!,
            subscriptionKey: subscriptionKey,
        })

        try {
            // Convert Observable to Promise using firstValueFrom
            const response: ProcessTokenPaymentResponse = await firstValueFrom(this.http.post<ProcessTokenPaymentResponse>(this.urlProcessTokenPayment, post))
            console.log('ProcessTokenPaymentResponse', response)
            
            // Check the response and return accordingly
            if (response) {
                if (!response.error && (response.requestSubmitted === true || response.payGateResponse?.resultCode == PayGateResultCode['Auth Done'])) {
                    // Request submitted (nothing charged until the landlord accepts)
                    return response
                } else {
                    // Payment failed - log details and return the full response with error info
                    console.error('Payment processing failed:', {
                        error: response.error,
                        errorMessage: response.errorMessage,
                        resultCode: response.payGateResponse?.resultCode,
                        resultDescription: response.payGateResponse?.resultDescription,
                    })
                    return response
                }
            } else {
                // If response is not available, return an error object
                console.error('Empty response received from payment gateway')
                return { 
                    error: true, 
                    errorMessage: 'No response received from payment gateway. Please try again or contact support.' 
                }
            }
        } catch (error: any) {
            // Log and handle errors from HTTP request
            console.error('HTTP request failed:', error)
            return { 
                error: true, 
                errorMessage: error?.error?.errorMessage || error?.message || 'Payment request failed. Please try again or contact support.' 
            }
        }
    }

    httpFollowupRequest(activeClient: Client, payGateMpiResponse: PayGateMpiResponse) {
        return new Promise<any>((resolve) => {
            let payGateClient = new PayGateClient({
                firstName: activeClient.firstName,
                lastName: activeClient.lastName,
                email: activeClient.$email,
                mobile: activeClient.$phoneNumber,
                key: activeClient.$key,
            })

            let post = new FollowupRequestRequest({
                payGateClient: payGateClient,
                payRequestId: payGateMpiResponse.payRequestId,
            })

            this.http.post<FollowupRequestResponse>(this.urlFollowupRequest, post).subscribe((response: FollowupRequestResponse) => {
                resolve(response)
            })
        })
    }

    httpApproveRequest(subscription: Subscription, paymentKey: string) {
        return new Promise<any>((resolve) => {
            //TODO: This will break if there are 2 payments on the sub
            let post = new SettlementRequestRequest({
                paymentKey: paymentKey,
            })

            this.http.post<SettlementRequestRequest>(this.httpSettlementRequestUrl, post).subscribe((response: SettlementRequestRequest) => {
                resolve(response)
            })
        })
    }

    /**
     * @param auditEvent Optional attributed event appended to the subscription's `eventLog`
     *   in the same write as the cancellation.
     */
    async clientCancelSubscription(subscription: Subscription, auditEvent?: Record<string, any> | null): Promise<{ error: boolean; errorType?: string; message?: string }> {
        // The server flips the status to clientCancelled (respecting an accepted or in-progress
        // request) and voids a hold only if one exists.
        const response = await this.backendService.httpVoidRequest(subscription.$key!, undefined, { cancelledBy: 'client', auditEvent })

        if (response?.error) {
            return { error: true, errorType: response.errorType, message: response.message || 'This request could not be cancelled. Please contact support.' }
        }

        await this.backendService.httpSendSlack({ message: `*Subscription Cancelled* - By Client: ${subscription.denormClientFirstName} ${subscription.denormClientLastName}` })
        return { error: false }
    }
}
